import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { DangerButton, SecondaryButton } from '../../../components/buttons';
import { Loading, ThinLoadingBar } from '../../../components/loading';
import { Modal } from '../../../components/modal';
import { useOrders } from '../../../hooks/orders';
import { useProducts } from '../../../hooks/products';
import { useVendor } from '../../../hooks/vendors';
import { orderEditRoute, storeLayout } from '../../../route-apis';
import { BuyupView } from './components/buyup-view';
import { OrderView } from './components/order-view';
import { SyncIndicator } from './components/sync-indicator';
import { useEditOrderView } from './hooks/use-edit-order-view';
import type { EditOrderPopulatedProperties } from './types';

export function EditOrder() {
  const navigate = useNavigate();
  const { store } = storeLayout.useRouteContext();
  const { orderId } = orderEditRoute.useParams();
  const { orders } = useOrders();
  const order = useMemo(() => orders.find((order) => order.id === orderId), [orders, orderId]);
  const { vendor } = useVendor(order?.vendor_id);
  const { products } = useProducts();
  const vendorProducts = useMemo(() => {
    if (!products || !vendor) {
      return [];
    }

    return products.filter((product) => product.vendor_id === vendor.vendor_id);
  }, [products, vendor]);

  const canEdit =
    order !== undefined &&
    order.store_id === store.id &&
    vendor !== undefined &&
    products.length > 0;

  // Bounce to the order list when the order can't be edited — most often right
  // after cancelling it, which drops it from the orders cache while this route
  // is still mounted.
  //
  // This must fire exactly once. Every router state change re-renders us while
  // the navigation is in flight, and each new navigation cancels the pending
  // one, so a redirect that repeats on every render never settles: the route
  // stays mounted, re-renders, redirects again, and the page locks up. Hence
  // an effect with stable deps rather than <Navigate>, which re-navigates
  // whenever its props object identity changes — i.e. on every render.
  useEffect(() => {
    if (!canEdit) {
      navigate({ to: '/stores/$storeId/orders', params: { storeId: store.id }, replace: true });
    }
  }, [canEdit, navigate, store.id]);

  if (!canEdit) {
    return <Loading />;
  }

  return (
    <EditOrderPopulated order={order} vendor={vendor} products={vendorProducts} store={store} />
  );
}

function EditOrderPopulated(properties: EditOrderPopulatedProperties) {
  const view = useEditOrderView(properties);

  const View = view.isBuyup ? BuyupView : OrderView;

  return (
    <>
      <div className="grid grid-cols-3 items-center px-4 py-2">
        <div />
        <h1 className="text-2xl font-semibold justify-self-center">{view.vendor.name} Order</h1>
        <SyncIndicator sync={view.sync} />
      </div>
      <View view={view} />
      {/* Driven by `visible`, never by a mount guard: `Modal` wraps a Headless UI
          Transition, and unmounting it outright skips the leave animation and
          tears the Dialog, its Portal, and the nested DemoCTAModal down in one
          synchronous commit instead of letting Headless UI close them in order. */}
      <Modal
        title="Purchase Order"
        visible={view.showSubmitOrder}
        onClose={view.toggleShowSubmitOrder}
        footer={view.review.footer}
      >
        {view.review.body}
      </Modal>
      <Modal
        title="Cancel Order"
        visible={view.showCancelConfirm}
        onClose={view.dismissCancelConfirm}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={view.dismissCancelConfirm}>Keep Order</SecondaryButton>
            <DangerButton onClick={view.confirmDeleteOrder}>Cancel Order</DangerButton>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to cancel this order? This cannot be undone.
        </p>
      </Modal>
      <Modal
        title="Reset Order"
        visible={view.showResetConfirm}
        onClose={view.dismissResetConfirm}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={view.dismissResetConfirm}>Keep Changes</SecondaryButton>
            <DangerButton onClick={view.confirmResetOrder}>Reset Order</DangerButton>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          This will reset changes you have made to {view.resetItemCount} item(s) this order. Are you
          sure you want to continue?
        </p>
      </Modal>
      {view.productSaving && <ThinLoadingBar />}
    </>
  );
}
